import React, { FC, useRef, useCallback, useState } from 'react'
import { change, Field, FieldArray, initialize, InjectedFormProps, reduxForm, submit } from 'redux-form'
import { Form, Collapse, Row, Col, Button } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { DragDropContext, SensorAPI, useTouchSensor, useMouseSensor } from 'react-beautiful-dnd'
import { RootState } from '../../../reducers'

// atoms
import InputField from '../../../atoms/InputField'
import TypeRadioGroupField from '../../../atoms/TypeRadioGroupField'
import SelectField from '../../../atoms/SelectField'
import ColorPickerField from '../../../atoms/ColorPickerField'
import SwitchField from '../../../atoms/SwitchField'
import DragAndDropOptions from '../../../atoms/DragAndDropOptions'

// assets
import { ReactComponent as ChevronIcon } from '../../../assets/icons/BaseIcons/chevron-down-1-icon.svg'
import { ReactComponent as HeightIcon } from '../../../assets/icons/BaseMiniIcons/height-icon.svg'
import { ReactComponent as WidthIcon } from '../../../assets/icons/BaseMiniIcons/width-icon.svg'
import { ReactComponent as TrashIcon } from '../../../assets/icons/BaseIcons/trash-1-icon.svg'

// interfaces
import { IFormItemData } from '../../../types/interfaces'

// utils
import { ELEMENT_ATTRIBUTES, ELEMENT_TYPES, FORM, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT, RADIO_BTN_GROUP_FIELD } from '../../../utils/enums'

// modals
import SQLEditorModal from '../../modals/SQLEditorModal'
import ValidationModal from '../../modals/ValidationModal'

const { Panel } = Collapse

export type ComponentProps = {
	onRemoveItem: Function
}
type Props = InjectedFormProps<IFormItemData, ComponentProps> &
	ComponentProps & {
		handleSubmit: Function
		elementType?: string
	}

// render block of options
const RenderOptions = ({ fields }: any) => {
	const { t } = useTranslation()
	const sensorApiRef = useRef<SensorAPI>()
	const dispatch = useDispatch()

	const disable = useSelector((state: RootState) => state.formTemplate.disable)

	const mySensor = useCallback((api: SensorAPI) => {
		sensorApiRef.current = api
	}, [])
	return (
		<DragDropContext
			/* check if drop destination is the same as source destination = only 1 column */
			onDragEnd={async (dropResult) => {
				if (!dropResult.source || !dropResult.destination) {
					return
				}
				/* move option from source index to destination index */
				await fields.move(dropResult.source.index, dropResult.destination.index)
				dispatch(submit(FORM.DROPDOWNBUTTON_ELEMENT_FORM))
			}}
			sensors={[mySensor, useTouchSensor, useMouseSensor]}
			enableDefaultSensors={false}
		>
			<DragAndDropOptions key={0} data={fields} disabled={disable} formType={FORM.DROPDOWNBUTTON_ELEMENT_FORM} />
			<Button
				className={'btn btn--transparent h-25px my-2 flex items-center shadow-none bg-gray-25'}
				icon={<PlusOutlined />}
				onClick={() => fields.push({})}
				disabled={disable}
			>
				{t('loc:Add option')}
			</Button>
		</DragDropContext>
	)
}

const DropdownButtonElementForm: FC<Props> = (props) => {
	const { handleSubmit, onRemoveItem } = props
	const dispatch = useDispatch()
	const [isSQLEditorModalShowed, setSQLEditorModalShowed] = useState(false)
	const disable = useSelector((state: RootState) => state.formTemplate.disable)
	const columns = useSelector((state: RootState) => state.formTemplate.columns)
	const formItemId = useSelector((state: RootState) => state.form?.[FORM.DROPDOWNBUTTON_ELEMENT_FORM]?.values?.i)

	const [isValidationModalShowed, setValidationModalShowed] = useState(false)
	const formValues = useSelector((state: RootState) => state.form?.[FORM.DROPDOWNBUTTON_ELEMENT_FORM]?.values)
	const { t } = useTranslation()

	return (
		<>
			<Form layout={'vertical'} onSubmitCapture={handleSubmit}>
				<Row className={'mx-4 mb-4'} justify={'space-between'}>
					<h3>{t('loc:Dropdown')}</h3>
					<TrashIcon className={'trashIcon'} onClick={() => onRemoveItem(formItemId)} />
				</Row>
				<Row className={'ml-4 mr-4 mb-4'} justify={'center'}>
					<Col span={24}>
						<Field
							component={SelectField}
							label={t('loc:Column in table')}
							getPopupContainer={(triggerNode: any) => triggerNode}
							name={ELEMENT_ATTRIBUTES.TABLE_COLUMN}
							options={columns?.data}
							size={'medium'}
							disabled={disable}
						/>
					</Col>
				</Row>
				<Collapse expandIcon={({ isActive }) => <ChevronIcon rotate={isActive ? 0 : 180} />} expandIconPosition={'end'} ghost>
					<Panel header={t('loc:Options')} key='1'>
						<FieldArray name='options' component={RenderOptions} />
						<Row justify={'center'} className={'w-full'}>
							<Col span={11}>
								<p className={'leading-8'}>{t('loc:Displayed options')}</p>
							</Col>
							<Col span={2} />
							<Col span={11}>
								<Field
									component={InputField}
									placeholder={t('loc:Enter options')}
									name={ELEMENT_ATTRIBUTES.DISPLAYED_OPTIONS}
									size={'medium'}
									disabled={disable}
								/>
							</Col>
						</Row>
						<Button
							className={'unfilled-btn w-full h-25px mb-2'}
							disabled={disable}
							onClick={() => {
								setSQLEditorModalShowed(true)
								dispatch(
									initialize(FORM.SQL_EDITOR_FORM, {
										[ELEMENT_ATTRIBUTES.SQL]: formValues?.[ELEMENT_ATTRIBUTES.SQL] || '',
										[ELEMENT_ATTRIBUTES.LINKED_FIELDS]: formValues?.[ELEMENT_ATTRIBUTES.LINKED_FIELDS]
									})
								)
							}}
						>
							{t('loc:SQL editor')}
						</Button>
					</Panel>
					<Panel header={t('loc:Size')} key='2'>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={11}>
								<Field
									className={'element-width'}
									component={InputField}
									name={ELEMENT_ATTRIBUTES.COL}
									size={'medium'}
									suffix={<WidthIcon />}
									disabled={disable}
									maxLength={2}
								/>
							</Col>
							<Col span={2} />
							<Col span={11}>
								<Field
									className={'element-height'}
									component={InputField}
									name={ELEMENT_ATTRIBUTES.ROW}
									size={'medium'}
									suffix={<HeightIcon />}
									disabled={true}
									maxLength={3}
								/>
							</Col>
						</Row>
						<Row justify={'center'} className={'mb-2 w-full'}>
							<Col span={11} className={'h-34px flex flex-col justify-center'}>
								<p className={'m-0'}>{t('loc:Field size')}</p>
							</Col>
							<Col span={2} />
							<Col span={11} className={'h-34px'}>
								<Field
									component={TypeRadioGroupField}
									optionsType={RADIO_BTN_GROUP_FIELD.SIZE}
									name={ELEMENT_ATTRIBUTES.SIZE}
									size={'medium'}
									disabled={disable}
								/>
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Label')} key='3'>
						<Field component={InputField} placeholder={t('loc:Enter label')} name={ELEMENT_ATTRIBUTES.LABEL} size={'medium'} disabled={disable} />
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col className={'h-34px'} span={11}>
								<Field component={ColorPickerField} name={ELEMENT_ATTRIBUTES.LABEL_COLOR} size={'medium'} disabled={disable} />
							</Col>
							<Col span={2} />
							<Col span={11}>
								<Field
									component={TypeRadioGroupField}
									optionsType={RADIO_BTN_GROUP_FIELD.TEXT_TYPE}
									name={ELEMENT_ATTRIBUTES.LABEL_TEXT_TYPE}
									disabled={disable}
								/>
							</Col>
						</Row>
						<Row justify={'center'} className={'mb-2 w-full'}>
							<Col span={11} className={'h-34px flex flex-col justify-center'}>
								<p className={'m-0'}>{t('loc:Position')}</p>
							</Col>
							<Col span={2} />
							<Col span={11} className={'h-34px'}>
								<Field
									component={TypeRadioGroupField}
									optionsType={RADIO_BTN_GROUP_FIELD.POSITION}
									name={ELEMENT_ATTRIBUTES.LABEL_POSITION}
									size={'medium'}
									disabled={disable}
								/>
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Hint')} key='4'>
						<Field component={InputField} placeholder={t('loc:Hint text')} name={ELEMENT_ATTRIBUTES.HINT} size={'medium'} disabled={disable} />
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={11}>
								<Field component={ColorPickerField} name={ELEMENT_ATTRIBUTES.HINT_COLOR} size={'medium'} disabled={disable} />
							</Col>
							<Col span={2} />
							<Col span={11}>
								<Field
									component={TypeRadioGroupField}
									placeholder={t('loc:Enter template name')}
									optionsType={RADIO_BTN_GROUP_FIELD.TEXT_TYPE}
									name={ELEMENT_ATTRIBUTES.HINT_TEXT_TYPE}
									size={'medium'}
									disabled={disable}
								/>
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Validations')} key='5'>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={11} className={'h-34px flex flex-col justify-center'}>
								<p className={'m-0'}>{t('loc:Required')}</p>
							</Col>
							<Col span={2} />
							<Col span={11} className={'h-34px'}>
								<Field component={SwitchField} name={ELEMENT_ATTRIBUTES.REQUIRED} className={'float-right'} disabled={disable} />
							</Col>
						</Row>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={11} className={'h-34px flex flex-col justify-center'}>
								<p className={'m-0'}>{t('loc:Readonly')}</p>
							</Col>
							<Col span={2} />
							<Col span={11} className={'h-34px'}>
								<Field component={SwitchField} name={ELEMENT_ATTRIBUTES.READ_ONLY} className={'float-right'} disabled={disable} />
							</Col>
						</Row>
						<Button
							className={'unfilled-btn w-full h-25px mb-2'}
							disabled={disable}
							onClick={() => {
								dispatch(change(FORM.VALIDATION_FORM, ELEMENT_ATTRIBUTES.READ_ONLY, formValues?.readOnly))
								dispatch(change(FORM.VALIDATION_FORM, ELEMENT_ATTRIBUTES.REQUIRED, formValues?.required))
								setValidationModalShowed(true)
							}}
						>
							{t('loc:Validation options')}
						</Button>
					</Panel>
				</Collapse>
			</Form>
			<ValidationModal
				width={460}
				typeOfElementForm={FORM.DROPDOWNBUTTON_ELEMENT_FORM}
				isVisible={isValidationModalShowed}
				onCancel={() => {
					setValidationModalShowed(false)
				}}
				elementLabel={formValues?.label}
				elementType={ELEMENT_TYPES.DROPDOWN_BUTTON}
			/>
			<SQLEditorModal
				width={460}
				isVisible={isSQLEditorModalShowed}
				onCancel={() => {
					setSQLEditorModalShowed(false)
				}}
				typeOfElementForm={FORM.DROPDOWNBUTTON_ELEMENT_FORM}
			/>
		</>
	)
}

const form = reduxForm<IFormItemData, ComponentProps>({
	form: FORM.DROPDOWNBUTTON_ELEMENT_FORM,
	forceUnregisterOnUnmount: true,
	touchOnChange: true,
	destroyOnUnmount: true,
	onChange: debounce((values, dispatch, props) => {
		if (props.anyTouched) {
			props.submit()
		}
	}, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT)
})(DropdownButtonElementForm)

export default form
